/**
 * ============================================================================
 * REQUEST PASSWORD RESET COMPONENT
 * ============================================================================
 * 
 * Step 1: Request password reset OTP
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Button from '../common/Button';
import Input from '../common/Input';
import LanguageSwitcher from '../common/LanguageSwitcher';
import { getPhoneValidation, formatPhoneInput, getFullPhoneNumber, validateCameroonPhone } from '../../utils/phoneValidation';

/**
 * Request Password Reset Component
 * @returns {JSX.Element}
 */
const RequestPasswordReset = () => {
  const navigate = useNavigate();
  const { requestPasswordReset, loading } = useAuth();
  const { showError, showSuccess } = useToast();
  const { language } = useI18n();

  const [formData, setFormData] = useState({
    countryCode: '+237', // Cameroon default
    phone: '',
  });

  const [errors, setErrors] = useState({});
  const [phoneValidation, setPhoneValidation] = useState(getPhoneValidation('+237'));

  useEffect(() => {
    setPhoneValidation(getPhoneValidation(formData.countryCode));
  }, [formData.countryCode]);

  const countryCodes = [
    { code: '+237', country: 'Cameroon', flag: '🇨🇲', name: language === 'fr' ? 'Cameroun' : 'Cameroon' },
    { code: '+254', country: 'Kenya', flag: '🇰🇪', name: 'Kenya' },
    { code: '+256', country: 'Uganda', flag: '🇺🇬', name: 'Uganda' },
    { code: '+255', country: 'Tanzania', flag: '🇹🇿', name: 'Tanzania' },
    { code: '+250', country: 'Rwanda', flag: '🇷🇼', name: 'Rwanda' },
    { code: '+234', country: 'Nigeria', flag: '🇳🇬', name: 'Nigeria' },
    { code: '+233', country: 'Ghana', flag: '🇬🇭', name: 'Ghana' },
  ];

  const handleChange = (field, value) => {
    if (field === 'phone') {
      const formatted = formatPhoneInput(value, formData.countryCode);
      setFormData((prev) => ({ ...prev, [field]: formatted }));
    } else if (field === 'countryCode') {
      setFormData((prev) => ({ ...prev, [field]: value, phone: '' }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    const cleanedPhone = formData.phone.replace(/\s/g, '');

    if (!cleanedPhone) {
      newErrors.phone = language === 'fr' 
        ? 'Le numéro de téléphone est requis' 
        : 'Phone number is required';
    } else {
      if (formData.countryCode === '+237') {
        const validation = validateCameroonPhone(cleanedPhone);
        if (!validation.valid) {
          newErrors.phone = validation.error;
        }
      } else {
        const validation = getPhoneValidation(formData.countryCode);
        if (!validation.pattern.test(cleanedPhone) || cleanedPhone.length !== validation.length) {
          newErrors.phone = validation.error;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const phoneNumber = getFullPhoneNumber(formData.phone, formData.countryCode);
      await requestPasswordReset({ phone: phoneNumber });
      
      showSuccess(
        language === 'fr'
          ? 'Code de réinitialisation envoyé!'
          : 'Reset code sent!'
      );
      
      // Navigate to reset password confirmation
      navigate('/auth/reset-password', { 
        state: { phone: phoneNumber } 
      });
    } catch (error) {
      const errorMessage = error?.message || error?.errors?.[0] || 
        (language === 'fr' 
          ? 'Échec de l\'envoi du code de réinitialisation' 
          : 'Failed to send reset code');
      showError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        {/* Language Switcher */}
        <div className="flex justify-end mb-4">
          <LanguageSwitcher />
        </div>

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors group"
          >
            <svg
              className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="font-medium">{language === 'fr' ? 'Retour' : 'Back'}</span>
          </button>
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-green-500 rounded-2xl mb-4 shadow-lg">
              <span className="text-3xl">🔑</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {language === 'fr' ? 'Réinitialiser le mot de passe' : 'Reset Password'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {language === 'fr' 
                ? 'Entrez votre numéro pour recevoir un code de réinitialisation' 
                : 'Enter your phone number to receive a reset code'}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Phone Number Input */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-6">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
              {language === 'fr' ? 'Numéro de téléphone' : 'Phone Number'}
            </label>
            <div className="flex gap-3">
              <div className="relative flex-shrink-0">
                <select
                  value={formData.countryCode}
                  onChange={(e) => handleChange('countryCode', e.target.value)}
                  className="px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none pr-10 font-medium min-w-[140px]"
                >
                  {countryCodes.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.code}
                    </option>
                  ))}
                </select>
                <svg
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder={phoneValidation.placeholder}
                  error={errors.phone}
                  required
                  className="w-full text-lg py-3"
                  inputMode="numeric"
                />
                {formData.phone && !errors.phone && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {language === 'fr' ? 'Exemple' : 'Example'}: {phoneValidation.example}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {language === 'fr' ? (
                <>
                  <strong>Note:</strong> Vous devez avoir un mot de passe configuré sur votre compte. 
                  Si vous n'avez pas de mot de passe, veuillez vous connecter avec votre numéro de téléphone.
                </>
              ) : (
                <>
                  <strong>Note:</strong> You must have a password set on your account. 
                  If you don't have a password, please login with your phone number.
                </>
              )}
            </p>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={loading}
            className="text-lg py-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
          >
            {language === 'fr' ? 'Envoyer le code →' : 'Send Reset Code →'}
          </Button>

          {/* Back to Login */}
          <div className="text-center pt-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {language === 'fr' ? 'Vous vous souvenez de votre mot de passe?' : 'Remember your password?'}{' '}
              <button
                type="button"
                onClick={() => navigate('/auth/login')}
                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 font-semibold underline"
              >
                {language === 'fr' ? 'Se connecter' : 'Login'}
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestPasswordReset;
